/**
 * FTUEFlow — First Time User Experience.
 *
 * Full-screen tap-to-advance flow matching the Figma prototype (Archimedes).
 * Uses absolute positioning to place cards and Muse at specific positions
 * that progressively reveal the three story building blocks:
 *   character → scene → story beats
 *
 * Figma: https://www.figma.com/proto/ZAmARUioy7itYtLL4q9VC6/Archimedes?node-id=1548-2858
 * Docs:  docs/features/FTUE.md
 */

import React, { useState, useEffect, useRef } from "react";
import { View, Text, Image, Pressable, Animated, StyleSheet, useWindowDimensions, AppState, Platform } from "react-native";
import type { AppStateStatus } from "react-native";
import { colors, textStyle, useScale, getOrbSize, centeredOverlay } from "../styles";
import { useFaceStore } from "../store/faceStore";
import { useFTUEStore } from "../store/ftueStore";
import { useSocketStore } from "../store/socketStore";
import { useStoryStore } from "../store/storyStore";
import { useComicStore } from "../store/comicStore";
import { useConfigStore } from "../store/configStore";
import { DEFAULT_IMAGE_STYLE, DEFAULT_OUTPUT_FORMAT } from "@amzn/a4k-creator-studio-shared";
import { trackView, trackButton, trackSession, Button, Session, View as MetricView } from "../services/metrics";
import { playMp3, stop as stopFTUEAudio, pause as pauseFTUEAudio, resume as resumeFTUEAudio, dispose as disposeFTUEAudio } from "../audio/FTUEAudioPlayer";
import storyBg from "../assets/story-bg.png";

import VoiceOrb from "./voice/VoiceOrb";
import ImageItemCard from "./story/ImageItemCard";
import GrownUpsBanner from "./GrownUpsBanner";
import GrownUpsModal from "./GrownUpsModal";
import { ACCESS_BLOCKED_TITLES } from "../store/errorMessages";
import { useKeepAwake } from "../hooks/useKeepAwake";

/* ── Constants ── */

const LAUNCH_BTN_BG = "#0F141A";

const ANIM = {
  SPLASH_FADE: 300,
  MUSE_APPEAR: 50,
  CARD_APPEAR: 400,
  CARD_FADE: 400,
  READY_FADE: 500,
  SPRING_FRICTION: 6,
  SPRING_FRICTION_POS: 8,
  INACTIVE_OPACITY: 0.8,
  INACTIVE_SCALE: 0.65,
  MUSE_INITIAL_SCALE: 0.05,
  AUDIO_DELAY_STEP1: 600,
  AUTO_ADVANCE_DELAY: 500,
  SPLASH_AUTO_ADVANCE: 2000,
  TAP_DEBOUNCE: 500,
} as const;

const LAYOUT = {
  CARD_SCALE: 2.21,       // Figma uniformScaleFactor when focused
  BEAT_NORMAL_W: 278,
  BEAT_NORMAL_H: 156,
  BEAT_FOCUSED_W: 327,
  BEAT_FOCUSED_H: 184,
  BEAT_TEXT_W: 327,
  BEATS_WRAP_W: 350,
  BEATS_WRAP_H: 280,
  STORYBUILDER_LOGO_W: 300,
  STORYBUILDER_LOGO_H: 76,
  AMAZON_KIDS_LOGO_W: 432,
  AMAZON_KIDS_LOGO_H: 106,
  LAUNCH_BTN_RADIUS: 32,
  LAUNCH_BTN_PADDING: 20,
  CREATE_ICON_W: 24,
  CREATE_ICON_H: 53,
  BEAT_NORMAL_BORDER_RADIUS: 24,
  BEAT_NORMAL_IMG_BORDER_RADIUS: 20,
  BEAT_FOCUSED_BORDER_RADIUS: 30,
  BEAT_FOCUSED_IMG_BORDER_RADIUS: 26,
  CARD_ROTATION_LEFT: "-8deg",
  CARD_ROTATION_RIGHT: "8deg",
  CARD_CHAR_LEFT: "5.2%",
  CARD_CHAR_TOP: "20.8%",
  CARD_SCENE_LEFT: "69.5%",
  CARD_SCENE_TOP: "18.7%",
  READY_SPACER: 221,
  BEATS_CENTER_OFFSET: 164,
  // Background beats cascade up-and-right off the focused beat (Figma 3857:17031):
  // each steps further right and higher so their upper-right corners fan out.
  BEAT_BACK_LEFT: 103,
  BEAT_BACK_TOP: 0,
  BEAT_MID_LEFT: 76,
  BEAT_MID_TOP: 24,
  BEAT_FOCUSED_TOP: 42,
  BEAT_BORDER_WIDTH: 4,
  BEAT_TEXT_PADDING: 8,
  LAUNCH_CONTENT_GAP: 6,
  LAUNCH_PRIMARY_FONT: 20,
  LAUNCH_SECONDARY_FONT: 14,
} as const;

/* ── Assets ── */
const imgFluffy = require("../assets/ftue/fluffy.png");
const imgOuterSpace = require("../assets/ftue/outer-space.png");
const beatImage = require("../assets/ftue/beat-image.png");
import AmazonKidsLogo from "../assets/icons/amazon-kids-logo.svg";
import StorybuilderLogo from "../assets/icons/storybuilder-logo.svg";
const createStoryIcon = require("../assets/icons/create-story.png");

// Audio: web require() returns URL string, native returns asset ID (resolved in FTUEAudioPlayer.native.ts)
const STEP_AUDIO: Record<number, string | number> = {
  1: require("../assets/audio/en/tiffany/step1-intro.mp3"),
  2: require("../assets/audio/en/tiffany/step2-characters.mp3"),
  3: require("../assets/audio/en/tiffany/step3-scenes.mp3"),
  4: require("../assets/audio/en/tiffany/step4-beats.mp3"),
  5: require("../assets/audio/en/tiffany/step5-ready.mp3"),
};

const TOTAL_STEPS = 6; // 0=splash, 1=muse, 2=character, 3=scene, 4=beats, 5=ready

/* ── Muse positions per step (fraction of container) ── */
const MUSE_POS = [
  { x: 0.58, y: 0.46 },  // step 0: right of center (Figma dot position)
  { x: 0.50, y: 0.38 },  // step 1: centered
  { x: 0.35, y: 0.36 },  // step 2: near character card (left)
  { x: 0.63, y: 0.33 },  // step 3: near scene card (right)
  { x: 0.29, y: 0.72 },  // step 4: near beats (lower-left)
  { x: 0.50, y: 0.44 },  // step 5: centered (ready screen)
];

/* ── Step animation logic ── */
interface StepAnims {
  splashOpacity: Animated.Value;
  museOpacity: Animated.Value;
  museScale: Animated.Value;
  charDim: Animated.Value;
  charScale: Animated.Value;
  sceneDim: Animated.Value;
  sceneScale: Animated.Value;
  beatsOpacity: Animated.Value;
  beatsScale: Animated.Value;
  readyOpacity: Animated.Value;
}

function animateStep(step: number, anims: StepAnims) {
  const { splashOpacity, museOpacity, museScale, charDim, charScale, sceneDim, sceneScale, beatsOpacity, beatsScale, readyOpacity } = anims;

  if (step === 0) {
    useFaceStore.getState().setVoiceActive(false);
  } else if (step === 1) {
    useFaceStore.getState().setVoiceActive(true);
    Animated.parallel([
      Animated.timing(splashOpacity, { toValue: 0, duration: ANIM.SPLASH_FADE, useNativeDriver: true }),
      Animated.timing(museOpacity, { toValue: 1, duration: ANIM.MUSE_APPEAR, useNativeDriver: true }),
      Animated.spring(museScale, { toValue: 1, friction: ANIM.SPRING_FRICTION, useNativeDriver: true }),
    ]).start();
  } else if (step === 2) {
    Animated.parallel([
      Animated.timing(charDim, { toValue: 0, duration: ANIM.CARD_APPEAR, useNativeDriver: true }),
      Animated.spring(charScale, { toValue: 1, friction: ANIM.SPRING_FRICTION, useNativeDriver: true }),
    ]).start();
  } else if (step === 3) {
    Animated.parallel([
      Animated.timing(charDim, { toValue: ANIM.INACTIVE_OPACITY, duration: ANIM.CARD_FADE, useNativeDriver: true }),
      Animated.timing(charScale, { toValue: ANIM.INACTIVE_SCALE, duration: ANIM.CARD_FADE, useNativeDriver: true }),
      Animated.timing(sceneDim, { toValue: 0, duration: ANIM.CARD_APPEAR, useNativeDriver: true }),
      Animated.spring(sceneScale, { toValue: 1, friction: ANIM.SPRING_FRICTION, useNativeDriver: true }),
    ]).start();
  } else if (step === 4) {
    Animated.parallel([
      Animated.timing(sceneDim, { toValue: ANIM.INACTIVE_OPACITY, duration: ANIM.CARD_FADE, useNativeDriver: true }),
      Animated.timing(sceneScale, { toValue: ANIM.INACTIVE_SCALE, duration: ANIM.CARD_FADE, useNativeDriver: true }),
      Animated.timing(beatsOpacity, { toValue: 1, duration: ANIM.CARD_APPEAR, useNativeDriver: true }),
      Animated.spring(beatsScale, { toValue: 1, friction: ANIM.SPRING_FRICTION, useNativeDriver: true }),
    ]).start();
  } else if (step === 5) {
    Animated.parallel([
      Animated.timing(charDim, { toValue: 1, duration: ANIM.SPLASH_FADE, useNativeDriver: true }),
      Animated.timing(sceneDim, { toValue: 1, duration: ANIM.SPLASH_FADE, useNativeDriver: true }),
      Animated.timing(beatsOpacity, { toValue: 0, duration: ANIM.SPLASH_FADE, useNativeDriver: true }),
      Animated.timing(readyOpacity, { toValue: 1, duration: ANIM.READY_FADE, useNativeDriver: true }),
    ]).start();
  }
}

export default function FTUEFlow() {
  const [step, setStep] = useState(0);
  useKeepAwake('ftue', step < TOTAL_STEPS - 1);
  const complete = useFTUEStore((s) => s.complete);
  const accessBlocked = useSocketStore((s) => s.accessBlocked);
  const [blockedModalVisible, setBlockedModalVisible] = useState(false);
  const { s, scale } = useScale();
  const { width: winW, height: winH } = useWindowDimensions();

  // Auto-dismiss the blocked modal if access is restored while it's open (e.g. a
  // parent scans the QR code and auto-polling reconnects) — otherwise the modal
  // keeps telling the user they're blocked after the banner has already cleared.
  useEffect(() => {
    if (!accessBlocked) setBlockedModalVisible(false);
  }, [accessBlocked]);

  // Animated values
  const museOpacity = useRef(new Animated.Value(0)).current;
  const museScale = useRef(new Animated.Value(ANIM.MUSE_INITIAL_SCALE)).current;
  const museX = useRef(new Animated.Value(MUSE_POS[0].x)).current;
  const museY = useRef(new Animated.Value(MUSE_POS[0].y)).current;
  const charDim = useRef(new Animated.Value(1)).current;
  const charScale = useRef(new Animated.Value(0.5)).current;
  const sceneDim = useRef(new Animated.Value(1)).current;
  const sceneScale = useRef(new Animated.Value(0.5)).current;
  const beatsOpacity = useRef(new Animated.Value(0)).current;
  const beatsScale = useRef(new Animated.Value(0.5)).current;
  const readyOpacity = useRef(new Animated.Value(0)).current;
  const splashOpacity = useRef(new Animated.Value(1)).current;

  useEffect(() => {
    useFaceStore.getState().setOrbMode("animated");
    animateStep(step, {
      splashOpacity, museOpacity, museScale,
      charDim, charScale, sceneDim, sceneScale,
      beatsOpacity, beatsScale, readyOpacity,
    });
  }, [step]);

  // Animate Muse position on step change
  useEffect(() => {
    const pos = MUSE_POS[step];
    if (step <= 0) {
      museX.setValue(pos.x);
      museY.setValue(pos.y);
    } else {
      Animated.parallel([
        Animated.spring(museX, { toValue: pos.x, friction: ANIM.SPRING_FRICTION_POS, useNativeDriver: true }),
        Animated.spring(museY, { toValue: pos.y, friction: ANIM.SPRING_FRICTION_POS, useNativeDriver: true }),
      ]).start();
    }
  }, [step]);

  // Clean up audio resources on unmount (e.g. error boundary, hot reload)
  useEffect(() => () => { disposeFTUEAudio(); }, []);

  // Play audio for each step and auto-advance after it finishes
  useEffect(() => {
    const url = STEP_AUDIO[step];
    if (!url) return;
    let cancelled = false;
    const delay = step === 1 ? ANIM.AUDIO_DELAY_STEP1 : 0;
    const timer = setTimeout(() => {
      if (cancelled) return;
      useFaceStore.getState().setFTUEVariant("02"); // talking face
      playMp3(url).then(() => {
        if (cancelled) return;
        useFaceStore.getState().setFTUEVariant("01"); // back to idle
        if (step > 0 && step < TOTAL_STEPS - 1) {
          setTimeout(() => { if (!cancelled) setStep(step + 1); }, ANIM.AUTO_ADVANCE_DELAY);
        }
      }).catch(() => {
        if (cancelled) return;
        useFaceStore.getState().setFTUEVariant("01");
      });
    }, delay);
    return () => { cancelled = true; clearTimeout(timer); stopFTUEAudio(); };
  }, [step]);

  // Splash (step 0) auto-advances to the Muse intro after a short delay
  useEffect(() => {
    if (step !== 0) return;
    const timer = setTimeout(() => setStep(1), ANIM.SPLASH_AUTO_ADVANCE);
    return () => clearTimeout(timer);
  }, [step]);

  const stepTime = useRef(Date.now());
  useEffect(() => { stepTime.current = Date.now(); trackView(MetricView.FTUE, String(step)); }, [step]);

  // Pause audio when app goes to background, restore state on resume (native only —
  // react-native-web's AppState polyfill has EventEmitter compatibility issues)
  useEffect(() => {
    if (Platform.OS === "web") return;
    const handler = (state: AppStateStatus) => {
      if (state === "active") {
        resumeFTUEAudio();
        useFaceStore.getState().setOrbMode("animated");
        useFaceStore.getState().setVoiceActive(step >= 1);
      } else {
        pauseFTUEAudio();
      }
    };
    const sub = AppState.addEventListener("change", handler);
    return () => sub.remove();
  }, [step]);

  const advance = () => {
    if (step >= 1 && Date.now() - stepTime.current < ANIM.TAP_DEBOUNCE) return;
    if (step < TOTAL_STEPS - 1) {
      trackButton(MetricView.FTUE, Button.FTUE.Continue, String(step + 1));
      setStep(step + 1);
    }
  };

  // Size Muse exactly as it appears on the story builder so it doesn't change
  // size across the FTUE → story builder transition.
  const orbSize = getOrbSize(scale);
  const bgSize = Math.max(winW, winH) * 1.1;

  return (
    <Pressable style={styles.root} onPress={advance} accessibilityRole="button" accessibilityLabel="Tap to continue">
      {/* ── Step 0-1: Splash logo (cross-fades out as Muse appears) ── */}
      {step <= 1 && (
        <Animated.View style={[styles.splashWrap, { opacity: splashOpacity }]}>
          <AmazonKidsLogo
            width={s(LAYOUT.AMAZON_KIDS_LOGO_W)}
            height={s(LAYOUT.AMAZON_KIDS_LOGO_H)}
            accessibilityLabel="Amazon Kids"
          />
        </Animated.View>
      )}

      {/* ── Character card (steps 2+) ── */}
      {step >= 2 && (
        <Animated.View
          style={[
            styles.cardImage,
            {
              left: LAYOUT.CARD_CHAR_LEFT, top: LAYOUT.CARD_CHAR_TOP,
              transform: [
                { translateX: charScale.interpolate({ inputRange: [ANIM.INACTIVE_SCALE, 1], outputRange: [-s(144 * LAYOUT.CARD_SCALE) * 0.175, 0], extrapolate: "clamp" }) },
                { scale: charScale },
                { rotate: LAYOUT.CARD_ROTATION_LEFT },
              ],
            },
          ]}
          accessibilityLabel="Fluffy"
        >
          <ImageItemCard label="Fluffy" imageSource={imgFluffy} scale={LAYOUT.CARD_SCALE} dimOpacity={charDim} />
        </Animated.View>
      )}

      {/* ── Scene card (steps 3+) ── */}
      {step >= 3 && (
        <Animated.View
          style={[
            styles.cardImage,
            {
              left: LAYOUT.CARD_SCENE_LEFT, top: LAYOUT.CARD_SCENE_TOP,
              transform: [
                { translateX: sceneScale.interpolate({ inputRange: [ANIM.INACTIVE_SCALE, 1], outputRange: [s(144 * LAYOUT.CARD_SCALE) * 0.175, 0], extrapolate: "clamp" }) },
                { scale: sceneScale },
                { rotate: LAYOUT.CARD_ROTATION_RIGHT },
              ],
            },
          ]}
          accessibilityLabel="Outer space"
        >
          <ImageItemCard label="Outer space" imageSource={imgOuterSpace} scale={LAYOUT.CARD_SCALE} dimOpacity={sceneDim} />
        </Animated.View>
      )}

      {/* ── Story beats (step 4) ── */}
      {step >= 4 && (
        <Animated.View style={[
          styles.beatsWrap,
          {
            width: s(LAYOUT.BEATS_WRAP_W), height: s(LAYOUT.BEATS_WRAP_H),
            left: "50%", top: "55%",
            marginLeft: -s(LAYOUT.BEATS_CENTER_OFFSET),
            opacity: beatsOpacity,
            transform: [{ scale: beatsScale }],
          },
        ]}>
          {/* Background beat cards (Normal state — image only, smaller).
              Cascade up-and-right: furthest-back is highest and furthest-right. */}
          <View style={[styles.beatCardNormal, { width: s(LAYOUT.BEAT_NORMAL_W), height: s(LAYOUT.BEAT_NORMAL_H), top: s(LAYOUT.BEAT_BACK_TOP), left: s(LAYOUT.BEAT_BACK_LEFT), borderRadius: s(LAYOUT.BEAT_NORMAL_BORDER_RADIUS) }]}>
            <Image source={beatImage} style={{ width: s(LAYOUT.BEAT_NORMAL_W), height: s(LAYOUT.BEAT_NORMAL_H), borderRadius: s(LAYOUT.BEAT_NORMAL_IMG_BORDER_RADIUS) }} resizeMode="cover" resizeMethod="resize" />
          </View>
          <View style={[styles.beatCardNormal, { width: s(LAYOUT.BEAT_NORMAL_W), height: s(LAYOUT.BEAT_NORMAL_H), top: s(LAYOUT.BEAT_MID_TOP), left: s(LAYOUT.BEAT_MID_LEFT), borderRadius: s(LAYOUT.BEAT_NORMAL_BORDER_RADIUS) }]}>
            <Image source={beatImage} style={{ width: s(LAYOUT.BEAT_NORMAL_W), height: s(LAYOUT.BEAT_NORMAL_H), borderRadius: s(LAYOUT.BEAT_NORMAL_IMG_BORDER_RADIUS) }} resizeMode="cover" resizeMethod="resize" />
          </View>
          {/* Focused beat (image frame + text) */}
          <View style={[styles.beatFocused, { width: s(LAYOUT.BEAT_FOCUSED_W), top: s(LAYOUT.BEAT_FOCUSED_TOP) }]}>
            <View style={[styles.beatImageFrame, { width: s(LAYOUT.BEAT_FOCUSED_W), height: s(LAYOUT.BEAT_FOCUSED_H), borderRadius: s(LAYOUT.BEAT_FOCUSED_BORDER_RADIUS), borderWidth: s(LAYOUT.BEAT_BORDER_WIDTH) }]}>
              <Image source={beatImage} style={[styles.beatFocusedImg, { borderRadius: s(LAYOUT.BEAT_FOCUSED_IMG_BORDER_RADIUS) }]} resizeMode="cover" resizeMethod="resize" />
            </View>
            <View style={[styles.beatTextWrap, { paddingHorizontal: s(LAYOUT.BEAT_TEXT_PADDING) }]}>
              <Text style={[styles.beatText, { fontSize: s(16), lineHeight: s(24) }]} numberOfLines={2}>
                Fluffy slid down the hill into a huge pile of leaves.
              </Text>
            </View>
          </View>
        </Animated.View>
      )}

      {/* ── Background (fades in on step 5) ── */}
      {step === 5 && (
        <Animated.Image source={storyBg} style={{ position: "absolute", width: bgSize, height: bgSize, alignSelf: "center", top: -(bgSize - winH) / 2, opacity: readyOpacity }} resizeMode="contain" />
      )}

      {/* ── Muse (always rendered for preload, hidden until step 1) ── */}
      <Animated.View
        style={[
          styles.museWrap,
          {
            width: orbSize,
            height: orbSize,
            opacity: museOpacity,
            transform: [
              { translateX: museX.interpolate({ inputRange: [0, 1], outputRange: [-orbSize / 2, winW - orbSize / 2] }) },
              { translateY: museY.interpolate({ inputRange: [0, 1], outputRange: [-orbSize / 2, winH - orbSize / 2] }) },
              { scale: museScale },
            ],
          },
        ]}
        pointerEvents="none"
      >
        <VoiceOrb size={orbSize} />
      </Animated.View>

      {/* ── Step 5: Ready screen ── */}
      {step === 5 && (
        <Animated.View style={[styles.readyWrap, { opacity: readyOpacity }]}>
          <StorybuilderLogo
            width={s(LAYOUT.STORYBUILDER_LOGO_W)}
            height={s(LAYOUT.STORYBUILDER_LOGO_H)}
            accessibilityLabel="Storybuilder"
          />
          <View style={{ height: s(LAYOUT.READY_SPACER) }} />
          <Pressable
            style={[styles.launchBtn, {
              borderRadius: s(LAYOUT.LAUNCH_BTN_RADIUS),
              paddingVertical: s(LAYOUT.LAUNCH_BTN_PADDING),
              paddingHorizontal: s(LAYOUT.LAUNCH_BTN_PADDING),
            }]}
            onPress={() => {
              // When access is blocked, the builder can't be entered (its socket is
              // intentionally disconnected, which would strand the user on an infinite
              // loading spinner). Surface the same parental-consent QR modal as the
              // banner's "Scan Code" instead of completing the FTUE. Auto-polling keeps
              // running in the background; once access is granted the banner clears and
              // this button resumes navigating to the builder.
              if (accessBlocked) {
                setBlockedModalVisible(true);
                return;
              }
              disposeFTUEAudio();
              useFaceStore.getState().resetAll();
              // Start a clean new story, mirroring the landing "Create new story" path.
              // An existing customer on a new device still sees the FTUE (needsFTUE is
              // per-device), so clear any local/server story state rather than assuming
              // it's empty. Flags are set synchronously, before StoryBuilder mounts —
              // its mount effect reads them once and bails if unset.
              useStoryStore.getState().clearAll();
              useComicStore.getState().clearAll();
              useConfigStore.getState().replaceAll({ imageStyle: DEFAULT_IMAGE_STYLE, outputFormat: DEFAULT_OUTPUT_FORMAT });
              useSocketStore.getState().socket?.emit("clearStory");
              useSocketStore.setState({ autoStartVoice: true, entryGreeting: "new" });
              trackSession(Session.FTUEComplete);
              complete();
            }}
            accessibilityRole="button"
            accessibilityLabel="Create a new story"
          >
            <View style={[styles.launchContent, { gap: s(LAYOUT.LAUNCH_CONTENT_GAP) }]}>
              <Image
                source={createStoryIcon}
                style={{ width: s(LAYOUT.CREATE_ICON_W), height: s(LAYOUT.CREATE_ICON_H) }}
                resizeMode="contain"
              />
              <View>
                <Text style={[styles.launchPrimary, { fontSize: s(LAYOUT.LAUNCH_PRIMARY_FONT) }]}>Create a new story</Text>
                <Text style={[styles.launchSecondary, { fontSize: s(LAYOUT.LAUNCH_SECONDARY_FONT) }]}>Let your imagination go wild</Text>
              </View>
            </View>
          </Pressable>
        </Animated.View>
      )}

      {/* ── Access banner (ready screen only) ──
          Appears once the FTUE is done (the "Create a new story" screen) if the
          VPC/Settings check blocked access. Scoped to the final step so it never
          overlaps the animated tutorial steps; App.tsx suppresses the global
          banner while FTUE is showing to avoid a duplicate. */}
      {step === TOTAL_STEPS - 1 && accessBlocked && (
        <GrownUpsBanner title={ACCESS_BLOCKED_TITLES[accessBlocked]} />
      )}
      <GrownUpsModal visible={blockedModalVisible} onClose={() => setBlockedModalVisible(false)} />
    </Pressable>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: colors.bg,
  },

  /* Splash */
  splashWrap: {
    ...centeredOverlay,
  },

  /* Cards (character / scene) — rendered directly as images */
  cardImage: {
    position: "absolute",
  },

  /* Beats */
  beatsWrap: {
    position: "absolute",
  },
  beatCardNormal: {
    position: "absolute",
    borderWidth: 3,
    borderColor: colors.labelSelected,   // #9B82F3 — non-focused beat border (Figma 3857:17031)
    shadowColor: colors.black,
    shadowOffset: { width: 0, height: 5 },
    shadowOpacity: 0.2,
    shadowRadius: 10,
    elevation: 4,
    overflow: "hidden",
  },
  beatFocused: {
    position: "absolute",
    left: 0,
  },
  beatImageFrame: {
    borderColor: colors.borderFocus,   // #6243ED grape — focused beat border (Figma 3857:17031)
    shadowColor: colors.black,
    shadowOffset: { width: 0, height: 5 },
    shadowOpacity: 0.2,
    shadowRadius: 30,
    elevation: 8,
    overflow: "hidden",
  },
  beatFocusedImg: {
    width: "100%",
    height: "100%",
  },
  beatTextWrap: {
    marginTop: 8,
  },
  beatText: {
    ...textStyle.body,
    color: colors.text,
  },

  /* Muse */
  museWrap: {
    position: "absolute",
  },

  /* Ready screen — Figma node 1548:3018 */
  readyWrap: {
    ...centeredOverlay,
    paddingBottom: 40,
  },
  launchBtn: {
    backgroundColor: LAUNCH_BTN_BG,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
  },
  launchContent: {
    flexDirection: "row",
    alignItems: "center",
  },
  launchPrimary: {
    ...textStyle.heading,
    color: colors.white,
  },
  launchSecondary: {
    ...textStyle.body,
    color: colors.white,
    opacity: 0.7,
    marginTop: 2,
  },
});
